import { FC, useState } from 'react'
import { Button, Link, Stack, Typography } from '@mui/material'

// 起動時の認証確認が 403 (アクセス拒否) で落ちたときに、ログインフォームの代わりに出す
// ゲート。EmbedSessionGate (401 用) と対称の位置づけで、403 は埋め込み固有ではない
// (単体起動の cert ドメインでも起きる) ため anycompass ではなく auth に置く。
//
// /login へ送ってはならない。403 は資格情報の問題ではないので、ログインし直しても
// 同じ 403 に戻るだけになる (log/193 work-log/6 §3-3)。
//
// 403 は 2 種類あるが、レスポンスからは判別できない (どちらも同一ボディ):
//   - 接続に必要な情報が載らなかった瞬間的なもの → 実測で 1〜3 分内に自然回復する。
//     主ボタン「再試行」で拾う
//   - 端末とアカウントの組が合っていない → 再試行では直らない。副リンク
//     「別のアカウントでログイン」が唯一の出口 (ゲートは全画面を覆うので、
//     これが無いと抜ける手段が消える)
// 判別できないので文言は出し分けず、両方の出口を常に置く (松田さん 2026-08-22 承認)。
//
// 「証明書」という語は出さない。証明書の導入は利用者の作業ではないので、手順ではなく
// 連絡先に寄せる (pages/login.tsx の certError と同じ方針)。
interface AccessDeniedGateProps {
  // 「再試行」の押下ハンドラ (= 認証確認の refetch)。成功すればゲートは親側の条件で下りる。
  onRetry: () => Promise<unknown>
}

export const AccessDeniedGate: FC<AccessDeniedGateProps> = ({ onRetry }) => {
  const [retrying, setRetrying] = useState(false)

  const handleRetry = async () => {
    setRetrying(true)
    try {
      await onRetry()
    } finally {
      setRetrying(false)
    }
  }

  return (
    <Stack
      height="100vh"
      direction="column"
      spacing={2}
      alignItems="center"
      justifyContent="center"
      px={3}
    >
      <Typography variant="h6" component="h1">
        Kakeru に接続できませんでした
      </Typography>
      <Typography variant="body2" color="text.secondary" textAlign="center">
        時間をおいて、もう一度お試しください。解決しない場合はサポート窓口にご連絡ください。
      </Typography>
      <Button variant="contained" disabled={retrying} onClick={handleRetry}>
        再試行
      </Button>
      {/*
        素の <a> (フルロード) にする。SPA 遷移だと認証 query が 403 エラーを保持したまま
        /login に着き、ゲートが出続けて画面に入れない。文書ごと読み直せば query は白紙から
        始まり、接続自体のやり直しにもなる。
      */}
      <Link href="/login" variant="body2" color="text.secondary">
        別のアカウントでログイン
      </Link>
    </Stack>
  )
}
